import React from "react";
import "../../styles/ContractorLogo.css";

/**
 * Uniform colour for contractor initials placeholders.
 * Single brand blue for a clean, consistent look across all cards.
 */
const COLOUR_PAIRS = [
  ["#005EB8", "#FFFFFF"], // brand blue
];

/**
 * Extract up to 2 initials from a business name.
 * "Bob's Plumbing" → "BP", "Acme" → "A"
 */
const getInitials = (name) => {
  if (!name) return null;
  const words = name
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return null;
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Pick a deterministic colour pair based on contractor ID.
 */
const getColourPair = (id) => {
  const idx = typeof id === "number" ? id % COLOUR_PAIRS.length : 0;
  return COLOUR_PAIRS[idx];
};

/**
 * ContractorLogo — shows the real logo when available, or a professional
 * initials placeholder when not.
 *
 * Props:
 *  - contractor: { id, business_name, business_logo, thumbnail_url, width, height }
 *  - className: applied to the outer wrapper (for sizing / shape)
 *  - imgClassName: applied to the <img> tag when a logo exists
 *  - size: "sm" | "md" | "lg" — sets the initials font-size preset
 *  - style: optional inline styles on the outer wrapper
 */
const ContractorLogo = ({
  contractor,
  className = "",
  imgClassName = "",
  size = "md",
  style,
  alt,
}) => {
  const logoSrc = contractor?.thumbnail_url || contractor?.business_logo;
  const hasLogo = !!logoSrc;

  if (hasLogo) {
    return (
      <div className={`contractor-logo-outer ${className}`} style={style}>
        <img
          src={logoSrc}
          alt={
            alt !== undefined
              ? alt
              : `${contractor.business_name || "Contractor"} logo`
          }
          width={contractor?.width || 120}
          height={contractor?.height || 60}
          className={`contractor-logo-real ${imgClassName}`}
          draggable="false"
          loading="lazy"
          onError={(e) => {
            // Hide the broken image and show initials fallback
            e.target.style.display = "none";
            const fallback = e.target.nextElementSibling;
            if (fallback) fallback.style.display = "flex";
          }}
        />
        {/* Hidden initials fallback — shown on image load error */}
        <InitialsPlaceholder
          contractor={contractor}
          size={size}
          hidden
        />
      </div>
    );
  }

  return (
    <div className={`contractor-logo-outer ${className}`} style={style}>
      <InitialsPlaceholder contractor={contractor} size={size} />
    </div>
  );
};

/**
 * Small building icon SVG for contractors with no name.
 * Renders inline so it inherits the parent's colour via currentColor.
 */
const BuildingIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="1em"
    height="1em"
    aria-hidden="true"
  >
    <path d="M3 21V7l9-4 9 4v14h-7v-5h-4v5H3Zm2-2h3v-5h8v5h3V8.1l-7-3.12L5 8.1V19Zm3-7h2v-2H8v2Zm4 0h2v-2h-2v2Zm-4-4h2V6H8v2Zm4 0h2V6h-2v2Z" />
  </svg>
);

const InitialsPlaceholder = ({ contractor, size, hidden }) => {
  const [bg, fg] = getColourPair(contractor?.id);
  const initials = getInitials(contractor?.business_name);

  const sizeClass =
    size === "sm"
      ? "contractor-initials--sm"
      : size === "lg"
      ? "contractor-initials--lg"
      : "contractor-initials--md";

  return (
    <div
      className={`contractor-initials ${sizeClass}`}
      style={{
        backgroundColor: bg,
        color: fg,
        display: hidden ? "none" : "flex",
      }}
      aria-label={contractor?.business_name || "Contractor"}
    >
      {initials ? <span>{initials}</span> : <BuildingIcon />}
    </div>
  );
};

export default ContractorLogo;
export { getInitials, getColourPair, COLOUR_PAIRS };
